import { useState, useMemo } from 'react'
import type { TransformerLoadingResult, LoadingBasis } from '../../types/engineering.ts'
import { LOADING_COLORS } from '../../types/engineering.ts'
import { fmtKw, fmtPct, fmtPhase } from '../../utils/format.ts'

interface TransformerLoadingTableProps {
  transformers: TransformerLoadingResult[]
  /** The avg-demand request parameter (kW) that produced these rows — the flat
   *  per-silent-meter adder on the hybrid basis and the per-meter model
   *  estimate. Tooltips derive from it so the copy can never drift from the
   *  request (B4). */
  avgDemandKw: number
  /** Read-once seed for the status filter, from a Home deep link such as
   *  /engineering?status=overloaded (UX spec §3.8). The filter stays internal
   *  state afterwards — this only chooses its initial value. */
  initialStatusFilter?: string
}

type SortKey = 'name' | 'kva_rating' | 'meter_count' | 'loading_pct'
type SortDir = 'asc' | 'desc'

const STATUS_BG: Record<string, string> = {
  overloaded: 'bg-red-50',
  heavy: 'bg-yellow-50',
  normal: '',
  underloaded: 'bg-blue-50',
  no_rating: 'bg-gray-50',
}

/** Client-side page size for the transformer table (D2). The endpoint returns
 *  every transformer in one payload; rendering ~4k rows is what made this tab
 *  crawl. */
const PAGE_SIZE = 50

/** Default filter value: the actionable set — overloaded + heavy (D2). A
 *  sentinel rather than a status, so it can never collide with a real
 *  `loading_status` value or with Stage 2's `divergent`. */
const NEEDS_ATTENTION = 'needs_attention'
const NEEDS_ATTENTION_STATUSES: ReadonlyArray<string> = ['overloaded', 'heavy']

/** Per-row basis chip: every transformer row is labeled measured, hybrid, or
 *  est. on EVERY tab (spec §3.4 — basis badge in every transformer table). */
function BasisChip({ basis, avgDemandKw }: { basis: LoadingBasis; avgDemandKw: number }) {
  if (basis === 'hybrid') {
    // Measured coincident peak + a flat avg-demand estimate per silent meter.
    return (
      <span
        className="inline-block mr-1 px-1 py-0.5 text-[10px] leading-none rounded font-normal align-middle"
        style={{ backgroundColor: '#fffbeb', color: '#b45309' }}
        title={`Hybrid: measured coincident peak + a flat ${avgDemandKw} kW estimate added for each silent, non-reporting meter — deliberately conservative`}
      >
        meas+est
      </span>
    )
  }
  if (basis === 'measured') {
    return (
      <span
        className="inline-block mr-1 px-1 py-0.5 text-[10px] leading-none rounded font-normal align-middle"
        style={{ backgroundColor: '#f5f3ff', color: '#7c3aed' }}
        title="AMI measured peak over the window (every meter reporting)"
      >
        meas.
      </span>
    )
  }
  return (
    <span
      className="inline-block mr-1 px-1 py-0.5 text-[10px] leading-none rounded bg-gray-100 text-gray-500 font-normal align-middle"
      title={`Model estimate (meter count × ${avgDemandKw} kW) — no AMI measured load passing coverage`}
    >
      est.
    </span>
  )
}

/** A7 (spec §3.9): amber Δ chip on rows where the model estimate and the AMI
 *  measurement disagree by more than the configured threshold — usually a bad
 *  kVA rating, wrong meter links, or a dead meter. */
function DivergenceChip() {
  return (
    <span
      className="inline-block mr-1 px-1 py-0.5 text-[10px] leading-none rounded font-medium align-middle"
      style={{ backgroundColor: '#fef3c7', color: '#b45309' }}
      title="Model and measurement disagree — usually a wrong kVA rating, wrong meter links, or a dead meter. Worth a look."
    >
      Δ
    </span>
  )
}

/** True when this row's displayed loading should come from the hybrid
 *  effective basis (measured/hybrid with a computed effective value), matching
 *  the /map/transformers/loading endpoint. Older payloads that predate the
 *  overlay (no loading_basis) stay on the legacy choose_load number. */
function usesEffective(t: TransformerLoadingResult): boolean {
  return (
    t.loading_basis != null &&
    t.loading_basis !== 'model' &&
    t.effective_loading_pct != null
  )
}

/** The loading % actually shown in the "Loading" column / used for sorting:
 *  the conservative effective (measured + est.) value for measured/hybrid rows,
 *  else the legacy choose_load loading_pct. */
function rowLoadingPct(t: TransformerLoadingResult): number | null {
  return usesEffective(t) ? t.effective_loading_pct : t.loading_pct
}

/** The status that drives the row color, background, pill and filter: the
 *  server's raw-classified effective status for measured/hybrid rows (never
 *  re-derived from the rounded number), else the legacy loading_status. */
function rowLoadingStatus(t: TransformerLoadingResult): string {
  return usesEffective(t)
    ? (t.effective_loading_status ?? t.loading_status)
    : t.loading_status
}

export function TransformerLoadingTable({
  transformers,
  avgDemandKw,
  initialStatusFilter,
}: TransformerLoadingTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('loading_pct')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  // Seeded once from the deep link; the select owns it from then on. The deep
  // link WINS over the D2 default — /engineering?status=divergent must not land
  // the user on Needs attention — so NEEDS_ATTENTION is the plain-visit
  // fallback only.
  const [statusFilter, setStatusFilter] = useState<string>(
    initialStatusFilter ?? NEEDS_ATTENTION,
  )
  const [page, setPage] = useState(0)

  const filtered = useMemo(() => {
    let items = transformers
    if (statusFilter === NEEDS_ATTENTION) {
      // D2: the actionable set. Keys off the basis-aware rowLoadingStatus, never
      // the raw loading_status, so a hybrid row is judged on its effective value.
      items = items.filter(t => NEEDS_ATTENTION_STATUSES.includes(rowLoadingStatus(t)))
    } else if (statusFilter === 'divergent') {
      // A7 (spec §3.9): rows where the model and measured bases disagree.
      items = items.filter(t => t.model_measured_divergent)
    } else if (statusFilter) {
      items = items.filter(t => rowLoadingStatus(t) === statusFilter)
    }
    return items
  }, [transformers, statusFilter])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va: number | string | null
      let vb: number | string | null
      switch (sortKey) {
        case 'name': va = a.name ?? ''; vb = b.name ?? ''; break
        case 'kva_rating': va = a.kva_rating ?? 0; vb = b.kva_rating ?? 0; break
        case 'meter_count': va = a.meter_count; vb = b.meter_count; break
        case 'loading_pct': va = rowLoadingPct(a) ?? -1; vb = rowLoadingPct(b) ?? -1; break
        default: va = 0; vb = 0
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [filtered, sortKey, sortDir])

  // Client-side pagination (50/page, D2). Clamp instead of effect-resetting so a
  // shrinking result set can never leave the page index out of range; filter
  // changes reset explicitly in the select's onChange.
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const paged = useMemo(
    () => sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [sorted, safePage],
  )

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const arrow = (key: SortKey) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">
          Transformers ({sorted.length})
        </h3>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0) }}
          className="text-xs border border-gray-300 rounded px-2 py-1"
        >
          <option value={NEEDS_ATTENTION}>Needs attention</option>
          <option value="">All Statuses</option>
          <option value="overloaded">Overloaded</option>
          <option value="heavy">Heavy</option>
          <option value="normal">Normal</option>
          <option value="underloaded">Underloaded</option>
          <option value="no_rating">No Rating</option>
          <option value="divergent">Model disagrees</option>
        </select>
      </div>

      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
              <th className="pb-2 pr-3 cursor-pointer hover:text-gray-700" onClick={() => handleSort('name')}>
                Name{arrow('name')}
              </th>
              <th className="pb-2 pr-3 text-right cursor-pointer hover:text-gray-700" onClick={() => handleSort('kva_rating')}>
                kVA{arrow('kva_rating')}
              </th>
              <th className="pb-2 pr-3">Phase</th>
              <th className="pb-2 pr-3 text-right cursor-pointer hover:text-gray-700" onClick={() => handleSort('meter_count')}>
                Meters{arrow('meter_count')}
              </th>
              <th className="pb-2 pr-3 text-right">Load kW</th>
              <th className="pb-2 pr-3 text-right">Model est. %</th>
              <th className="pb-2 pr-3 text-right">Measured max %</th>
              <th className="pb-2 pr-3 text-right cursor-pointer hover:text-gray-700" onClick={() => handleSort('loading_pct')}>
                Loading{arrow('loading_pct')}
              </th>
              <th className="pb-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(t => {
              // Number, color, background, pill and sort all key off the SAME
              // basis-aware values so a hybrid row can never show the amber
              // 'meas+est' chip next to the legacy model-fallback number/color.
              const status = rowLoadingStatus(t)
              const pct = rowLoadingPct(t)
              return (
              <tr key={t.id} className={`border-b border-gray-100 ${STATUS_BG[status] || ''}`}>
                <td className="py-1.5 pr-3 text-gray-800">
                  {t.name || t.gis_id || `#${t.id}`}
                  {t.feeder_name && (
                    <span className="text-xs text-gray-400 ml-1">({t.feeder_name})</span>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-right text-gray-600">{t.kva_rating ?? '—'}</td>
                <td className="py-1.5 pr-3 text-gray-600">{fmtPhase(t.phase)}</td>
                <td className="py-1.5 pr-3 text-right text-gray-600">{t.meter_count}</td>
                <td className="py-1.5 pr-3 text-right text-gray-600" title={`${t.estimated_kw} kW`}>
                  {fmtKw(t.estimated_kw)}
                </td>
                <td
                  className="py-1.5 pr-3 text-right text-gray-500"
                  title={t.model_loading_pct !== null ? `${t.model_loading_pct}%` : undefined}
                >
                  {fmtPct(t.model_loading_pct)}
                </td>
                <td
                  className="py-1.5 pr-3 text-right text-gray-500"
                  title={t.measured_loading_pct !== null ? `${t.measured_loading_pct}%` : undefined}
                >
                  {fmtPct(t.measured_loading_pct)}
                </td>
                <td
                  className="py-1.5 pr-3 text-right font-medium"
                  style={{ color: LOADING_COLORS[status] }}
                  title={pct !== null ? `${pct}%` : undefined}
                >
                  {t.model_measured_divergent && <DivergenceChip />}
                  <BasisChip basis={t.loading_basis ?? t.load_source} avgDemandKw={avgDemandKw} />
                  {fmtPct(pct)}
                </td>
                <td className="py-1.5">
                  <span
                    className="inline-block px-2 py-0.5 text-xs rounded-full font-medium"
                    style={{
                      backgroundColor: LOADING_COLORS[status] + '20',
                      color: LOADING_COLORS[status],
                    }}
                  >
                    {status.replace(/_/g, ' ')}
                  </span>
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {sorted.length === 0 && (
        <div className="text-xs text-gray-400 py-3 text-center">
          {statusFilter === NEEDS_ATTENTION
            ? 'No transformers are overloaded or heavy — choose "All Statuses" to see the full list.'
            : 'No transformers match this filter.'}
        </div>
      )}

      {sorted.length > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <span>
            Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="px-2 py-0.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Prev
            </button>
            <span className="px-1">
              {safePage + 1} / {pageCount}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              className="px-2 py-0.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
